import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Package, ShoppingCart, TrendingUp, Clock, LogOut, Plus,
  Pencil, Trash2, ExternalLink, Loader2, Copy, Settings as SettingsIcon,
} from "lucide-react";
import {
  adminMe, adminLogout, adminFetchStats, fetchProducts,
  adminDeleteProduct, adminFetchOrders, adminUpdateOrderStatus,
  fetchSettings, adminUpdateSettings,
} from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "../../hooks/use-toast";
import ProductForm from "../../components/admin/ProductForm";

const statusColors = {
  pending: "bg-amber-100 text-amber-700",
  processing: "bg-blue-100 text-blue-700",
  shipped: "bg-purple-100 text-purple-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};
const statusLabels = {
  pending: "Pendente", processing: "Em processamento", shipped: "Enviada",
  delivered: "Entregue", cancelled: "Cancelada",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [settings, setSettings] = useState({ metaPixelId: "", tiktokPixelId: "" });
  const [savingSettings, setSavingSettings] = useState(false);

  const loadAll = () => {
    Promise.all([fetchProducts(), adminFetchStats(), adminFetchOrders(), fetchSettings()])
      .then(([p, s, o, st]) => {
        setProducts(p);
        setStats(s);
        setOrders(o);
        setSettings({ metaPixelId: st.metaPixelId || "", tiktokPixelId: st.tiktokPixelId || "" });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const copyAddress = (o) => {
    const c = o.customer || {};
    const text = `${c.firstName} ${c.lastName}\n${c.address}\n${c.zip} ${c.city}\n${c.phone}\n${c.email}`;
    navigator.clipboard.writeText(text);
    toast({ title: "Morada copiada", description: "Cola no formulário do fornecedor." });
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await adminUpdateSettings(settings);
      toast({ title: "Definições guardadas", description: "Os pixels serão aplicados aos visitantes." });
    } catch {
      toast({ title: "Erro ao guardar", variant: "destructive" });
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    adminMe()
      .then(loadAll)
      .catch(() => navigate("/admin"));
  }, [navigate]);

  const logout = async () => {
    try {
      await adminLogout();
    } catch (e) {
      console.error("Logout error", e);
    }
    navigate("/admin");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este produto?")) return;
    try {
      await adminDeleteProduct(id);
      toast({ title: "Produto eliminado" });
      loadAll();
    } catch {
      toast({ title: "Erro ao eliminar", variant: "destructive" });
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await adminUpdateOrderStatus(id, status);
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      toast({ title: "Estado atualizado" });
    } catch {
      toast({ title: "Erro ao atualizar", variant: "destructive" });
    }
  };

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (p) => { setEditing(p); setFormOpen(true); };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const statCards = [
    { icon: Package, label: "Produtos", value: stats?.totalProducts ?? 0 },
    { icon: ShoppingCart, label: "Encomendas", value: stats?.totalOrders ?? 0 },
    { icon: TrendingUp, label: "Receita", value: `€ ${(stats?.revenue ?? 0).toFixed(2)}` },
    { icon: Clock, label: "Pendentes", value: stats?.pendingOrders ?? 0 },
  ];

  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Top bar */}
      <header className="bg-card border-b border-border sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif text-2xl tracking-[0.2em]">RADIX</span>
            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
              Ver loja <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <Button variant="outline" size="sm" onClick={logout} className="rounded-full">
              <LogOut className="h-4 w-4 mr-1.5" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((s) => (
            <div key={s.label} className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{s.label}</span>
                <s.icon className="h-5 w-5 text-primary" />
              </div>
              <p className="text-2xl font-semibold mt-2">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-border">
          {[
            { id: "products", label: "Produtos" },
            { id: "orders", label: "Encomendas" },
            { id: "settings", label: "Definições" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Products */}
        {tab === "products" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium">{products.length} produtos</h2>
              <Button onClick={openNew} className="rounded-full">
                <Plus className="h-4 w-4 mr-1.5" /> Novo produto
              </Button>
            </div>
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary text-left">
                    <tr>
                      <th className="p-3 font-medium">Produto</th>
                      <th className="p-3 font-medium hidden md:table-cell">Categoria</th>
                      <th className="p-3 font-medium">Preço</th>
                      <th className="p-3 font-medium hidden lg:table-cell">Custo / Lucro</th>
                      <th className="p-3 font-medium hidden sm:table-cell">Stock</th>
                      <th className="p-3 font-medium text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={p.id} className="border-t border-border hover:bg-secondary/40">
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <img src={p.images?.[0]} alt="" className="h-10 w-10 rounded object-cover bg-secondary" />
                            <div className="min-w-0">
                              <p className="font-medium line-clamp-1">{p.name}</p>
                              <p className="text-xs text-muted-foreground">{p.brand}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 capitalize hidden md:table-cell text-muted-foreground">{p.category}</td>
                        <td className="p-3">€ {p.price?.toFixed(2)}</td>
                        <td className="p-3 hidden lg:table-cell text-xs">
                          {p.supplierCost != null ? (
                            <span>
                              <span className="text-muted-foreground">€ {p.supplierCost.toFixed(2)}</span>
                              {" · "}
                              <span className="text-green-600 font-medium">+€ {(p.price - p.supplierCost).toFixed(2)}</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="p-3 hidden sm:table-cell">
                          <span className={p.stock > 10 ? "text-green-600" : "text-amber-600"}>{p.stock}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openEdit(p)} className="h-8 w-8 rounded-md hover:bg-secondary flex items-center justify-center" title="Editar">
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button onClick={() => handleDelete(p.id)} className="h-8 w-8 rounded-md hover:bg-destructive/10 text-destructive flex items-center justify-center" title="Eliminar">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Orders */}
        {tab === "orders" && (
          <div>
            <h2 className="text-lg font-medium mb-4">{orders.length} encomendas</h2>
            {orders.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground">
                Ainda não há encomendas.
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} className="bg-card border border-border rounded-xl p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="font-mono text-sm font-medium">#{o.id.slice(0, 8).toUpperCase()}</p>
                        <p className="text-sm text-muted-foreground">
                          {o.customer?.firstName} {o.customer?.lastName} · {o.customer?.email}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {o.customer?.address}, {o.customer?.city} {o.customer?.zip} · {o.customer?.phone}
                        </p>
                        <button
                          onClick={() => copyAddress(o)}
                          className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                        >
                          <Copy className="h-3.5 w-3.5" /> Copiar morada (para o fornecedor)
                        </button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold">€ {o.total?.toFixed(2)}</span>
                        <Select value={o.status} onValueChange={(v) => handleStatusChange(o.id, v)}>
                          <SelectTrigger className={`w-40 h-9 rounded-full text-xs font-medium border-0 ${statusColors[o.status] || ""}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.keys(statusLabels).map((s) => (
                              <SelectItem key={s} value={s}>{statusLabels[s]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="border-t border-border pt-3 flex flex-wrap gap-3">
                      {o.items?.map((item) => (
                        <div key={item.id} className="flex items-center gap-2 text-sm bg-secondary/50 rounded-lg px-2 py-1">
                          <img src={item.image} alt="" className="h-8 w-8 rounded object-cover" />
                          <span className="text-muted-foreground">{item.qty}x</span>
                          <span className="line-clamp-1 max-w-[160px]">{item.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings */}
        {tab === "settings" && (
          <div className="max-w-2xl">
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center gap-2 mb-1">
                <SettingsIcon className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-medium">Pixels de rastreamento (Anúncios)</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Cola aqui os IDs dos teus pixels. Serão aplicados automaticamente a todos os
                visitantes da loja, com os eventos PageView, ViewContent, AddToCart, InitiateCheckout e Purchase.
              </p>

              <div className="space-y-5">
                <div>
                  <Label>Meta Pixel ID (Facebook / Instagram)</Label>
                  <Input
                    value={settings.metaPixelId}
                    onChange={(e) => setSettings((s) => ({ ...s, metaPixelId: e.target.value }))}
                    placeholder="ex: 1234567890123456"
                    className="mt-1.5 h-11"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Obtém em business.facebook.com → Gestor de Eventos.
                  </p>
                </div>
                <div>
                  <Label>TikTok Pixel ID</Label>
                  <Input
                    value={settings.tiktokPixelId}
                    onChange={(e) => setSettings((s) => ({ ...s, tiktokPixelId: e.target.value }))}
                    placeholder="ex: CABCDE12345"
                    className="mt-1.5 h-11"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Obtém em ads.tiktok.com → Assets → Events.
                  </p>
                </div>
                <Button onClick={saveSettings} disabled={savingSettings} className="rounded-full">
                  {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar definições"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ProductForm
        open={formOpen}
        onOpenChange={setFormOpen}
        product={editing}
        onSaved={loadAll}
      />
    </div>
  );
}
